/**
 * `REVIEW.DAT`'s 52 debriefing messages, transcribed.
 *
 * The MISSION DEBRIEFING screen's narrative paragraph is not composed at
 * runtime by the original: `PS.EXE` picks one of these pre-written messages and
 * draws it.
 *
 * **The stored runs are not screen rows.** Each message below is a list of the
 * NUL-separated runs the file keeps it as; the original **joins them with a
 * space and re-flows** the paragraph to the panel. The evidence is that
 * `docs/game/ui-reference/mission-debriefing.png` draws
 * `REVIEW_NARRATIVES[1][0]` broken after *"…were shot"* and *"…shoot down any"*,
 * where the runs end after *"…were shot down,"* and *"…any enemy aircraft."*.
 * See `packages/formats/src/review-dat.ts`'s module note, which pins the
 * difference, and `apps/web`'s `career-screens.ts`, which does the joining.
 *
 * **Generated from the real file, checked in, and re-checked every run.**
 * `tools/bake/test/debrief-review.test.ts` decodes `VOLUME.006`'s `REVIEW.DAT`
 * with `packages/formats`' `decodeReviewDat` and asserts this table equals it
 * exactly, so the transcription cannot drift from the game data. Same split as
 * `historic-encounters.ts` — the decoder is tested on structure, the prose is
 * pinned once, here.
 *
 * `%t` (the mission's target) and `%n` (a famous ace's name) are the original's
 * own substitutions, left unexpanded here. `debrief-review.ts` fills them in.
 *
 * Section order is the file's, and it is exactly `MISSION_TYPES`' order with the
 * famous-ace duel appended — a correspondence neither list was built to have
 * (`MISSION_TYPES` came from `RBMB.EXE`'s Mission Builder labels), so each is
 * independent evidence for the other. `debrief-review.ts` asserts the mapping.
 */

/** One message: its stored runs, in order. Join with a space; not screen rows. */
export type ReviewNarrative = readonly string[];

/**
 * Eleven sections, in `REVIEW.DAT`'s own order — the ten mission families of
 * `MISSION_TYPES`, then the famous-ace duel.
 */
export const REVIEW_NARRATIVES: readonly (readonly ReviewNarrative[])[] = [
  // 0 — dogfight
  [
    // [0.0]
    [
      "Your flight did not locate the specified",
      "enemy fighters.  The mission was without result.",
    ],
    // [0.1]
    [
      "No aircraft from either side were shot down",
      "in the dogfight. The mission was without result.",
    ],
    // [0.2]
    [
      "Your flight cleared all of the enemy fighters",
      "from the sky without losing a single airplane!",
      "The mission was an outstanding success.",
    ],
    // [0.3]
    [
      "None of the enemy fighters were shot down.",
      "The mission was a failure.",
    ],
    // [0.4]
    [
      "Some of the enemy fighters were shot down.",
      "The mission was a partial success.",
    ],
    // [0.5]
    [
      "All of the enemy fighters were shot down.",
      "The mission was a success.",
    ],
  ],
  // 1 — patrol
  [
    // [1.0]
    [
      "None of your flight's aircraft were shot down,",
      "nor did your flight shoot down any enemy aircraft.",
      "The mission was uneventful.",
    ],
    // [1.1]
    [
      "Your flight shot down one enemy aircraft.",
    ],
    // [1.2]
    [
      "Your flight shot down enemy aircraft",
      "without losing a single fighter.",
      "The mission was an outstanding success!",
    ],
    // [1.3]
    [
      "Your flight succeeded in shooting down",
      "some enemy aircraft.",
    ],
    // [1.4]
    [
      "Your flight suffered some losses and did not shoot",
      "down any enemy aircraft. The mission was a failure.",
    ],
  ],
  // 2 — escortRecon
  [
    // [2.0]
    [
      "All of our reconnaissance aircraft and fighters completed",
      "the mission safely. The mission was an outstanding success!",
    ],
    // [2.1]
    [
      "All of our reconnaissance aircraft completed the mission",
      "safely. The mission was a success.",
    ],
    // [2.2]
    [
      "Some of our reconnaissance aircraft completed the mission",
      "safely. The mission was a partial success.",
    ],
    // [2.3]
    [
      "All of our reconnaissance aircraft were shot down.",
      "The mission was a failure.",
    ],
  ],
  // 3 — escortBombers
  [
    // [3.0]
    [
      "The enemy %t was destroyed.",
      "All of our bombers and fighters returned safely.",
      "The mission was an outstanding success!",
    ],
    // [3.1]
    [
      "The enemy %t was destroyed.",
      "All of our bombers returned safely.",
      "The mission was a success.",
    ],
    // [3.2]
    [
      "The enemy %t was destroyed,",
      "however, we lost some of our bombers.",
      "The mission was a partial success.",
    ],
    // [3.3]
    [
      "Although the enemy %t was destroyed,",
      "all of our bombers were shot down.",
      "The mission was a partial success.",
    ],
    // [3.4]
    [
      "Although all of our bombers made it back safely,",
      "the enemy %t was not damaged.",
      "The mission was unsuccessful.",
    ],
    // [3.5]
    [
      "The enemy %t was not destroyed.",
      "Some of our bombers failed to return.",
      "The mission was a failure.",
    ],
    // [3.6]
    [
      "The enemy %t was not destroyed.",
      "In addition, all of our bombers were shot down.",
      "The mission was a disaster!",
    ],
  ],
  // 4 — stopBombers
  [
    // [4.0]
    [
      "Our %t was not bombed. All enemy bombers were shot down.",
      "None of our fighters were destroyed.",
      "The mission was an outstanding success!",
    ],
    // [4.1]
    [
      "Our %t survived and all enemy bombers were shot down.",
      "The mission was a success.",
    ],
    // [4.2]
    [
      "Our %t survived the bombing raid.",
      "The mission was a success.",
    ],
    // [4.3]
    [
      "Although our %t was destroyed, the enemy paid dearly--",
      "all enemy bombers were shot down.",
      "The mission was a partial failure.",
    ],
    // [4.4]
    [
      "Our %t was destroyed.",
      "The mission was a failure.",
    ],
  ],
  // 5 — destroyZeppelin
  [
    // [5.0]
    [
      "The Zeppelin was sent down in flames!",
      "The mission was an outstanding success!",
    ],
    // [5.1]
    [
      "The Zeppelin was sent down in flames!",
      "The mission was a success.",
    ],
    // [5.2]
    [
      "The enemy Zeppelin escaped unscathed.",
      "The mission was a failure.",
    ],
  ],
  // 6 — interceptZeppelin
  [
    // [6.0]
    [
      "The Zeppelin was sent down in flames, our %t is safe,",
      "and none of our fighters were lost.",
      "The mission was an outstanding success!",
    ],
    // [6.1]
    [
      "The Zeppelin was sent down in flames before it could",
      "drop its deadly cargo on our %t.",
      "The mission was a success.",
    ],
    // [6.2]
    [
      "Although your flight sent the Zeppelin down in flames,",
      "it was not before the Zeppelin dropped its deadly cargo on our %t.",
      "The mission was a partial failure.",
    ],
    // [6.3]
    [
      "The enemy Zeppelin destroyed our %t and escaped unscathed.",
      "The mission was a failure.",
    ],
  ],
  // 7 — balloonDefense
  [
    // [7.0]
    [
      "All of our balloons and fighters survived the enemy onslaught.",
      "The mission was an outstanding success!",
    ],
    // [7.1]
    [
      "All of our balloons survived the enemy onslaught.",
      "The mission was a success.",
    ],
    // [7.2]
    [
      "Some of our balloons survived the enemy onslaught.",
      "The mission was a partial success.",
    ],
    // [7.3]
    [
      "All of our balloons were flamed.",
      "The mission was a failure.",
    ],
  ],
  // 8 — balloonBusting
  [
    // [8.0]
    [
      "All of the enemy balloons were flamed.",
      "All of our fighters returned safely.",
      "The mission was an outstanding success!",
    ],
    // [8.1]
    [
      "All of the enemy balloons were flamed.",
      "The mission was a success.",
    ],
    // [8.2]
    [
      "Some of the enemy balloons were destroyed.",
      "The mission was a partial success.",
    ],
    // [8.3]
    [
      "The enemy balloons survived.",
      "The mission was a failure.",
    ],
  ],
  // 9 — scramble
  [
    // [9.0]
    [
      "No aircraft from either side were shot down.",
      "The encounter with the enemy was inconclusive.",
    ],
    // [9.1]
    [
      "Your flight cleared all of the enemy fighters",
      "from the sky without losing a single aircraft!",
      "The mission was an outstanding success.",
    ],
    // [9.2]
    [
      "None of the enemy fighters were shot down.",
      "The mission was a failure.",
    ],
    // [9.3]
    [
      "Some of the enemy fighters were shot down.",
      "The mission was a partial success.",
    ],
    // [9.4]
    [
      "All of the enemy fighters were shot down.",
      "The mission was a success.",
    ],
  ],
  // 10 — aceDuel
  [
    // [10.0]
    [
      "You missed your aerial meeting with %n at the prearranged",
      "location.",
    ],
    // [10.1]
    [
      "Neither you nor %n survived your aerial engagement.",
    ],
    // [10.2]
    [
      "You shot down the legendary %n!",
    ],
    // [10.3]
    [
      "You were shot down by the legendary %n.",
    ],
    // [10.4]
    [
      "Your meeting with %n ended in a draw! Both of you",
      "survived the dogfight.",
    ],
  ],
];

/** 52, across eleven sections. Pinned so a bad edit to the table above fails loudly. */
export const REVIEW_NARRATIVE_COUNT = 52;
